// src/modules/app-views/goodsReceipt/pages/Document/modals/ItemModal/Step4Stock.jsx
// *******************************************************************************
// GOODS RECEIPT ITEM MODAL - STEP 2 (STOCK DISTRIBUTION)
// *******************************************************************************
// Distributes the converted quantity across root warehouses.
// User selects warehouse + quantity, adds to a table until fully distributed.
// Confirm button stays disabled until distribution matches convertedQuantity.
// If user goes back to Step1 and changes quantity, distribution is invalidated.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Flex, Typography, Select, InputNumber, Button, Tag, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import SimpleTable from 'components/SimpleComponents/SimpleTable';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import appService from 'services/appService';

const { Text } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const StockDistributionContent = ({ form, totalQuantity }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [distributions, setDistributions] = useState(() => {
    return form?.getFieldValue('stockDistribution') || [];
  });
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [entryQuantity, setEntryQuantity] = useState(0);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const warehouses = useMemo(() => buildWarehouseList(), []);

  const distributedTotal = distributions.reduce((sum, row) => sum + (row.quantity || 0), 0);
  const remaining = financialService.roundCurrency(totalQuantity - distributedTotal);
  const isBalanced = Math.abs(remaining) < 0.001;

  // Warehouse select options — disable already distributed ones
  const warehouseSelectOptions = useMemo(() => {
    const usedUids = distributions.map(d => d.stockUid);
    return warehouses.map(w => ({
      value: w.uid,
      label: (
        <Flex align="center" gap={6}>
          {w.color && (
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: w.color,
                flexShrink: 0,
              }}
            />
          )}
          <span>{w.code ? `${w.code}-${w.name}` : w.name}</span>
        </Flex>
      ),
      disabled: usedUids.includes(w.uid),
    }));
  }, [warehouses, distributions]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Set remaining quantity as default entry quantity
  useEffect(() => {
    if (remaining > 0) {
      setEntryQuantity(remaining);
    }
  }, [remaining]);

  // If only one warehouse exists, auto-select it
  useEffect(() => {
    if (warehouses.length === 1 && distributions.length === 0) {
      const w = warehouses[0];
      const newDist = [{
        key: Date.now(),
        stockUid: w.uid,
        stockCode: w.code,
        stockName: w.name,
        stockColor: w.color,
        quantity: totalQuantity,
      }];
      setDistributions(newDist);
      form?.setFieldsValue({ stockDistribution: newDist });
    }
  }, [warehouses.length]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(() => {
    if (!selectedWarehouse) return;
    if (!entryQuantity || entryQuantity <= 0) return;

    // Check if already distributed
    const existing = distributions.find(d => d.stockUid === selectedWarehouse);
    if (existing) {
      appService.message({ flag: 'w', text: 'item_already_exists' });
      return;
    }

    const warehouse = warehouses.find(w => w.uid === selectedWarehouse);
    if (!warehouse) return;

    const newRow = {
      key: Date.now(),
      stockUid: warehouse.uid,
      stockCode: warehouse.code,
      stockName: warehouse.name,
      stockColor: warehouse.color,
      quantity: entryQuantity,
    };

    const updated = [...distributions, newRow];
    setDistributions(updated);
    form?.setFieldsValue({ stockDistribution: updated });
    setSelectedWarehouse(null);
  }, [selectedWarehouse, entryQuantity, distributions, warehouses, form]);

  const handleRemove = useCallback(
    key => {
      const updated = distributions.filter(row => row.key !== key);
      setDistributions(updated);
      form?.setFieldsValue({ stockDistribution: updated });
    },
    [distributions, form],
  );

  const columns = [
    {
      title: translateX('warehouse'),
      dataIndex: 'stockUid',
      render: (_, record) => {
        const codeLabel = record.stockCode ? `${record.stockCode}-${record.stockName}` : record.stockName;
        return (
          <Flex align="center" gap={8}>
            {record.stockColor && (
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: record.stockColor,
                  flexShrink: 0,
                }}
              />
            )}
            <Text>{codeLabel}</Text>
          </Flex>
        );
      },
    },
    {
      title: translateX('entry_qty'),
      dataIndex: 'quantity',
      width: 120,
      align: 'right',
      render: value => (
        <Text strong>{financialService.formatQuantity(value)}</Text>
      ),
    },
    {
      title: '',
      width: 40,
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title={translateX('confirm_delete')}
          onConfirm={() => handleRemove(record.key)}
          okText={translateX('yes')}
          cancelText={translateX('no')}
        >
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header: total to distribute */}
      <Flex justify="start" align="center" style={{ marginBottom: 16 }}>
        <Flex align="center" gap={8}>
          <InboxOutlined style={{ fontSize: 16 }} />
          <Text strong>{translateX('stock_distribution')}</Text>
        </Flex>
        <Tag color={isBalanced ? 'success' : 'warning'} style={{ fontSize: 13, padding: '2px 10px', marginLeft: 16 }}>
          {financialService.formatQuantity(distributedTotal)} / {financialService.formatQuantity(totalQuantity)}
        </Tag>
      </Flex>

      {/* Input row: warehouse select + quantity + add button — always visible */}
      <Flex gap={8} align="end" style={{ marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            {translateX('warehouse')}
          </Text>
          <Select
            value={selectedWarehouse}
            onChange={setSelectedWarehouse}
            options={warehouseSelectOptions}
            placeholder={translateX('select_warehouse')}
            style={{ width: '100%', height: 40 }}
            allowClear
            disabled={isBalanced}
          />
        </div>

        <div style={{ width: 120 }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            {translateX('quantity')}
          </Text>
          <InputNumber
            value={entryQuantity}
            onChange={v => setEntryQuantity(v || 0)}
            min={0.01}
            max={remaining > 0 ? remaining : undefined}
            style={{ width: '100%', height: 40 }}
            disabled={isBalanced}
          />
        </div>

        <Button
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={isBalanced || !selectedWarehouse || !entryQuantity || entryQuantity <= 0}
          style={{ height: 40 }}
        >
          {translateX('distribute')}
        </Button>
      </Flex>

      {/* Distribution table — always visible */}
      <SimpleTable
        columns={columns}
        dataSource={distributions}
        rowKey="key"
        pagination={false}
        size="small"
        locale={{ emptyText: translateX('no_distribution') }}
      />

      {/* Remaining indicator */}
      {!isBalanced && distributions.length > 0 && (
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <Text type="warning">
            {translateX('remaining')}: {financialService.formatQuantity(remaining)}
          </Text>
        </div>
      )}
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const stockDistributionStep = () => ({
  stepTitle: 'stock_distribution',
  isConfirmStep: true,
  validationFields: [],
  content: ({ form }) => {
    const quantity = form?.getFieldValue('quantity') || 0;
    const conversionFactor = form?.getFieldValue('conversionFactor') || 1;
    const totalQuantity = financialService.roundCurrency(quantity * conversionFactor);

    return (
      <StockDistributionContent
        form={form}
        totalQuantity={totalQuantity}
      />
    );
  },
  onNext: ({ form }) => {
    // Validate distribution matches total converted quantity
    const distributions = form?.getFieldValue('stockDistribution') || [];
    const quantity = form?.getFieldValue('quantity') || 0;
    const factor = form?.getFieldValue('conversionFactor') || 1;
    const totalQuantity = financialService.roundCurrency(quantity * factor);
    const distributed = distributions.reduce((sum, row) => sum + (row.quantity || 0), 0);
    const remaining = Math.abs(totalQuantity - distributed);

    if (remaining > 0.001) {
      appService.message({ flag: 'w', text: 'distribution_incomplete' });
      return false;
    }
    return true;
  },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default stockDistributionStep;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Build flat list of root warehouses from parameters (no addressing children).
 */
function buildWarehouseList() {
  const raw = appService.getModulePreferences({
    moduleId: 'products',
    category: 'warehouses',
  });

  if (!raw || !Array.isArray(raw)) return [];

  return raw.map(w => ({
    uid: w.uid,
    code: w.code,
    name: w.name,
    color: w.color || '#1677ff',
    isDefault: w.isDefault || false,
  }));
}
